import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section id="categories" className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-center text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
            Popular Categories
          </span>
          <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            Browse the Basetao Spreadsheet by Category
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
            Jump straight to a category on the main website. Choose from the dropdown below or tap
            any product category card to continue your research.
          </p>
        </div>

        {/* Dropdown */}
        <div className="mt-10 flex flex-col items-center">
          <div className="relative w-full max-w-md" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-md border border-background-300 bg-background-50 px-5 py-3.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-400"
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span>Select a product category</span>
              <i
                className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`}
                aria-hidden="true"
              ></i>
            </button>

            {open && (
              <div className="absolute left-0 right-0 top-full z-20 max-h-[50vh] overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-foreground-950"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-sm text-foreground-400" aria-hidden="true"></i>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category grid */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all hover:border-primary-300 hover:bg-primary-50"
            >
              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md">
                <img
                  src={cat.image}
                  alt={`${cat.name} Basetao Spreadsheet category`}
                  title={`${cat.name} products`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="whitespace-nowrap font-heading text-sm font-semibold text-foreground-950">
                  {cat.name}
                </p>
                <p className="mt-0.5 truncate text-xs text-foreground-500">{cat.tagline}</p>
              </div>
              <i
                className="ri-arrow-right-up-line ml-auto text-base text-foreground-300 transition-colors group-hover:text-primary-600"
                aria-hidden="true"
              ></i>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}